"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import type { ActiveTab, SiblingTab, TabSource } from "../debug-panel.types";
import {
  getAutoSource,
  getTabContentSource,
  getTabLabel,
  isInsideInactiveTabsContent,
  resolveDebugComponent,
  sourcesEqual,
} from "../utils/tab-source";

export function useActiveTabs(): ActiveTab[] {
  const [entries, setEntries] = useState<ActiveTab[]>([]);
  const pathname = usePathname();
  useEffect(() => {
    const scan = () => {
      // Use [role="tab"][aria-selected="true"] instead of [data-state="active"] because
      // when ContextMenuTrigger asChild wraps TabsTrigger, Radix's data-state conflict
      // (issue #2096) causes ContextMenu's data-state="closed" to overwrite Tabs'
      // data-state="active". aria-selected is set via a separate ARIA code path and
      // is NOT subject to this overwrite — it reliably reflects tab selection state.
      const active = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[data-slot="tabs"] [role="tab"][aria-selected="true"]',
        ),
      );
      setEntries(
        active
          .filter((el) => {
            if (!getTabLabel(el)) return false;
            if (isInsideInactiveTabsContent(el)) return false;
            const rect = el.getBoundingClientRect();
            if (rect.width === 0 && rect.height === 0) return false;
            return true;
          })
          .map((el) => {
            const tabsEl = el.closest<HTMLElement>('[data-slot="tabs"]');
            const explicit = tabsEl?.dataset.debugSource;
            const source: TabSource | undefined = explicit
              ? { kind: "file", path: explicit }
              : tabsEl
                ? getAutoSource(tabsEl, pathname)
                : undefined;
            const contentSource = getTabContentSource(el, pathname);
            const siblingTabs: SiblingTab[] | undefined = tabsEl
              ? Array.from(tabsEl.querySelectorAll<HTMLElement>('[role="tab"]'))
                  .filter((sib) => sib !== el && getTabLabel(sib))
                  .map((sib): SiblingTab => {
                    const sibPath = resolveDebugComponent(sib, pathname);
                    return {
                      label: getTabLabel(sib),
                      isActive: sib.getAttribute("aria-selected") === "true",
                      ...(sibPath !== undefined ? { path: sibPath } : {}),
                    };
                  })
              : undefined;
            return {
              label: getTabLabel(el),
              ...(source !== undefined ? { source } : {}),
              ...(contentSource !== undefined ? { contentSource } : {}),
              ...(siblingTabs && siblingTabs.length > 0 ? { siblingTabs } : {}),
              ...(!sourcesEqual(source, contentSource) ? {} : {}),
            } satisfies ActiveTab;
          }),
      );
    };
    scan();
    const mo = new MutationObserver(scan);
    mo.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state", "aria-selected"],
      childList: true,
    });
    return () => mo.disconnect();
  }, [pathname]);
  return entries;
}
